import { useState } from 'react';
import { CalendarClock } from 'lucide-react';
import usePresence from '../../hooks/usePresence';
import { shouldShowContactAvailability } from '../../lib/professionalStatus';
import PlannerQuickAddModal from './PlannerQuickAddModal';

/**
 * MessageAvailabilityHint
 * -----------------------
 * Tiny inline hint that sits next to a Message button.
 *
 * Renders ONLY when the recipient's `contact_availability` is one of
 * the non-baseline values:
 *   - "limited"     -> "Limited availability — they'll still receive your message, but may reply later."
 *   - "unavailable" -> "Not available right now — they'll still receive your message, but may reply later."
 *
 * Data source: shared `usePresence` hook — the SAME endpoint that
 * powers the messenger pills. That means:
 *  - Zero additional fetches per profile page (the hook caches per id).
 *  - Privacy is enforced server-side (show_presence=false hides
 *    contact_availability and we render nothing).
 *  - `open` is the silent baseline — we render nothing, no noise.
 *
 * Also exposes a secondary action — "Set a reminder to message them
 * next week →" — that opens the EXISTING PlannerQuickAddModal
 * prefilled with sensible defaults (title "Message X", date today + 7
 * days, item_type follow_up, 1-day reminder). No new Planner API, no
 * new collection — pure reuse of the existing modal + endpoint.
 *
 * Does NOT disable or wrap the Message button — fully decoupled.
 */
export default function MessageAvailabilityHint({
  recipientId,
  recipientName,
  variant = 'block', // 'block' = full sentence under the button | 'inline' = compact single-line chip
  className = '',
}) {
  const { contactAvailability } = usePresence(recipientId);
  const [plannerOpen, setPlannerOpen] = useState(false);

  if (!recipientId) return null;
  if (!shouldShowContactAvailability(contactAvailability)) return null;

  const first = (recipientName || 'They').trim().split(/\s+/)[0] || 'They';
  const isLimited = contactAvailability === 'limited';
  const fullText = isLimited
    ? `${first} has limited availability right now — they'll still receive your message, but may reply later.`
    : `${first} is not available right now — they'll still receive your message, but may reply later.`;

  const tone = isLimited
    ? 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300'
    : 'border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300';
  const dot = isLimited ? 'bg-amber-500' : 'bg-rose-500';
  // The reminder link uses a muted variant of the same tone so it
  // sits inside the hint card without competing with the Message button.
  const linkTone = isLimited
    ? 'text-amber-700 dark:text-amber-200 hover:text-amber-900 dark:hover:text-amber-100'
    : 'text-rose-700 dark:text-rose-200 hover:text-rose-900 dark:hover:text-rose-100';

  const reminderAction = (
    <>
      <button
        type="button"
        onClick={() => setPlannerOpen(true)}
        className={`inline-flex items-center gap-1 text-[11px] font-semibold underline-offset-2 hover:underline ${linkTone}`}
        data-testid={`message-availability-reminder-${contactAvailability}`}
      >
        <CalendarClock className="h-3 w-3" aria-hidden="true" />
        Set a reminder to message them next week →
      </button>
      <PlannerQuickAddModal
        open={plannerOpen}
        onClose={() => setPlannerOpen(false)}
        context={{
          source: 'availability_hint',
          other_user_id: recipientId,
          other_user_name: recipientName || first,
        }}
      />
    </>
  );

  if (variant === 'inline') {
    return (
      <span
        className={`inline-flex items-center gap-1.5 text-[11px] leading-snug px-2 py-1 rounded-md border ${tone} ${className}`}
        data-testid={`message-availability-hint-${contactAvailability}`}
        role="note"
        aria-label={fullText}
      >
        <span className={`inline-block h-1.5 w-1.5 rounded-full ${dot}`} aria-hidden="true" />
        {isLimited ? 'May reply later — limited availability' : 'May reply later — not available right now'}
        <span aria-hidden="true" className="opacity-50">·</span>
        {reminderAction}
      </span>
    );
  }

  return (
    <div
      className={`flex items-start gap-2 text-xs leading-snug px-3 py-2 rounded-lg border ${tone} ${className}`}
      data-testid={`message-availability-hint-${contactAvailability}`}
      role="note"
    >
      <span className={`mt-1 inline-block h-1.5 w-1.5 rounded-full shrink-0 ${dot}`} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p>{fullText}</p>
        <p className="mt-1">{reminderAction}</p>
      </div>
    </div>
  );
}
